import { useState, useEffect, useRef } from 'react';
import { Box, IconButton, Button, Modal, Chip, Tooltip, Typography } from '@mui/material';
import { Colors } from '../../../../Styles/Colors';
import { withTranslation } from 'react-i18next';
import { ImagePaths } from '../../../../Utility/ImagePaths';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Loader from '../../../../Components/Loader';

const PreviousDiagnosis = (props) => {
    const [state, setState] = useState({
        selectedDate: props.dateList && props.dateList.length > 0 ? props.dateList[0] : ""
    });
    const [scrollPosition, setScrollPosition] = useState(0);
    const [maxScroll, setMaxScroll] = useState(0);


    const scrollRef = useRef(null);

    useEffect(() => {
        if (state.selectedDate && props.onDateChange) {
            props.onDateChange(state.selectedDate)
        }
    }, [state.selectedDate])

    useEffect(() => {
        // Calculate max scroll when component mounts or dateList changes
        if (scrollRef.current) {
            const maxScrollValue = scrollRef.current.scrollWidth - scrollRef.current.clientWidth;
            setMaxScroll(maxScrollValue);
        }
    }, [props.dateList]);

    const handleClosePopup = () => {
        props.closePopup()
    }

    const handleScroll = () => {
        if (scrollRef.current) {
            setScrollPosition(scrollRef.current.scrollLeft);

            // Update max scroll in case of window resize
            const newMaxScroll = scrollRef.current.scrollWidth - scrollRef.current.clientWidth;
            if (newMaxScroll !== maxScroll) {
                setMaxScroll(newMaxScroll);
            }
        }
    };

    const scroll = (direction) => {
        if (scrollRef.current) {
            const scrollAmount = 150;
            const newPosition = direction === "left"
                ? Math.max(scrollRef.current.scrollLeft - scrollAmount, 0)
                : Math.min(scrollRef.current.scrollLeft + scrollAmount, maxScroll);

            scrollRef.current.scrollTo({
                left: newPosition,
                behavior: "smooth"
            });
            setScrollPosition(newPosition);
        }
    };

    return (
        <Modal open={true} onClose={handleClosePopup}>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '60vw', bgcolor: 'white',
                boxShadow: 24, borderRadius: '0.2vw', height: '72vh', display: 'flex', flexDirection: 'column'
            }}>
                <Box component={'div'} p={'0.5vw'} height={"6vh"} display={'flex'} justifyContent={'space-between'} alignItems={'center'} borderBottom={"1px solid gray"}>
                    <Typography fontSize={'0.9vw'} fontWeight={600} color={Colors.SecondaryText}>Previous {props.title}</Typography>
                    <Button
                        className='Common_Btn_Min_Width'
                        onClick={handleClosePopup}
                    >
                        <Box component={'img'} src={ImagePaths.LabCloseButton.default} height={'1vw'} width={'1vw'} />
                    </Button>
                </Box>
                {props.dateList?.length > 0 ?
                    <Box width={"100%"} p="0.5vw" height={"65vh"} display="flex" flexDirection="column">
                        <Box width={"100%"} display="flex" alignItems="center" gap="0.5vw">
                            <IconButton
                                onClick={() => scroll("left")}
                                disabled={scrollPosition === 0}
                                sx={{ visibility: scrollPosition === 0 ? 'hidden' : 'visible' }}
                            >
                                <ArrowBackIosNewIcon fontSize="small" />
                            </IconButton>

                            <Box
                                ref={scrollRef}
                                sx={{
                                    display: "flex",
                                    gap: "0.5vw",
                                    overflowX: "auto",
                                    scrollBehavior: "smooth",
                                    minWidth: 0,
                                    whiteSpace: "nowrap",
                                    "&::-webkit-scrollbar": { display: "none" },
                                }}
                                onScroll={handleScroll}
                            >
                                {props.dateList?.map((item, index) => (
                                    <Chip
                                        key={index}
                                        className={`eMed_chip ${item === state.selectedDate ? 'eMed_Chip_OnClick' : 'eMed_Chip_Border'}`}
                                        size="medium"
                                        style={{ flexShrink: 0, minWidth: "6vw" }}
                                        label={item}
                                        variant={item === state.selectedDate ? 'contained' : 'outlined'}
                                        onClick={() => setState(prev => ({ ...prev, selectedDate: item }))}
                                        clickable
                                    />
                                ))}
                            </Box>

                            <IconButton
                                onClick={() => scroll("right")}
                                disabled={scrollPosition >= maxScroll}
                                sx={{ visibility: scrollPosition >= maxScroll ? 'hidden' : 'visible' }}
                            >
                                <ArrowForwardIosIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", mt: "0.5vw" }}>
                            {props.previousData?.length > 0 ? props.previousData?.map((item, index) => {
                                return (
                                    <Box key={`${item.appointment_id}_${index}`} border={"1px solid gray"} marginBottom={"0.5vw"}>
                                        <Box display={"flex"} alignItems={"center"} p={'0.5vw'} height={"1.8vw"} sx={{ background: "lightGray" }}>
                                            <Typography fontSize={'0.9vw'} fontWeight={600} color={Colors.SecondaryText}>OP Number : {item.appointment_id}</Typography>
                                            <IconButton style={{ marginLeft: "0.5vw" }} 
                                            disabled = {!((item?.icd_code?.length > 0) || (item?.diagnosis?.trim?.()?.length > 0))}
                                            onClick={(e) => {
                                                const copyBtn = e.currentTarget;
                                                copyBtn.disabled = true;
                                                props.onCopyDiagnosis(item);
                                                setTimeout(() => {
                                                    copyBtn.disabled = false;
                                                }, 1000);
                                            }}>
                                                <Tooltip placement='top' title='Copy'>
                                                    <ContentCopyIcon fontSize="small" 
                                                     color={ !((item?.icd_code?.length > 0) || (item?.diagnosis?.trim?.()?.length > 0)) ? "disabled" : "primary"} 
                                                    />
                                                </Tooltip>
                                            </IconButton>
                                        </Box>
                                        <Box display={'grid'} gridTemplateColumns={(props.isIcdCode && props.title2) ? '1fr 1fr' : '1fr'} gap={'0vw'} p={'0.5vw'}>
                                            {props.isIcdCode && <Box width={"100%"}>
                                                <Box display={"flex"} alignItems={"center"} p={'0.5vw'} height={"1.8vw"}>
                                                    <Typography fontSize={'0.9vw'} fontWeight={600} color={Colors.SecondaryText}>ICD Code</Typography>

                                                    {props.title2 ? <IconButton style={{ marginLeft: "0.5vw" }} disabled={!item?.icd_code || item?.icd_code?.length === 0}
                                                        onClick={(e) => {
                                                            const icdBtn = e.currentTarget;
                                                            icdBtn.disabled = true;
                                                            props.onCopyIcdCode(item?.icd_code);
                                                            setTimeout(() => {
                                                                icdBtn.disabled = false;
                                                            }, 1000);
                                                        }}
                                                    >
                                                        <Tooltip placement='top' title='Copy'>
                                                            <ContentCopyIcon fontSize="small"
                                                                color={!item?.icd_code || item?.icd_code?.length === 0 ? "disabled" : "primary"}
                                                            />
                                                        </Tooltip>
                                                    </IconButton> : null}
                                                </Box>
                                                <Box display={'flex'} flexWrap="wrap" gap={'0.3vw'}>
                                                    {(item?.icd_code && item?.icd_code.length > 0) ?
                                                        item?.icd_code?.map((code, idx) => (<Chip
                                                            className={`eMed_chip eMed_Chip_Border`}
                                                            size="medium"
                                                            style={{ width: "6vw" }}
                                                            label={code?.icd_code}
                                                            key={idx}
                                                            disabled
                                                        />))
                                                        : <Typography fontSize={'0.9vw'} color={Colors.SecondaryText} textAlign={"center"}>No Records Found</Typography>}
                                                </Box>
                                            </Box>}
                                            {props.title2 && <Box width={"100%"}>
                                                <Box display={"flex"} alignItems={"center"} p={'0.5vw'} height={"1.8vw"}>
                                                    <Typography fontSize={'0.9vw'} fontWeight={600} color={Colors.SecondaryText}>{props.title2}</Typography>
                                                    {props.isIcdCode && <IconButton style={{ marginLeft: "0.5vw" }} disabled={!item.diagnosis || item.diagnosis?.trim()?.length === 0}
                                                        onClick={(e) => {
                                                            const diaBtn = e.currentTarget;
                                                            diaBtn.disabled = true;
                                                            props.onCopyDiagnosisTxt(item.diagnosis);
                                                            setTimeout(() => {
                                                                diaBtn.disabled = false;
                                                            }, 1000);
                                                        }}
                                                    >
                                                        <Tooltip placement='top' title='Copy'>
                                                            <ContentCopyIcon fontSize="small"
                                                                color={!item.diagnosis || item.diagnosis?.trim()?.length === 0 ? "disabled" : "primary"}
                                                            />
                                                        </Tooltip>
                                                    </IconButton>}
                                                </Box>
                                                <Box
                                                    component="div"
                                                    sx={{
                                                        fontSize: '0.95vw',
                                                        p: "0.5vw",
                                                        wordBreak: 'break-word',
                                                        whiteSpace: "normal",
                                                        lineHeight: 1.5,
                                                        overflowWrap: "break-word",
                                                        "& div": { display: "block" },
                                                        "& b": { fontWeight: 600 }
                                                    }}
                                                    dangerouslySetInnerHTML={{__html: item.diagnosis || "-" }}
                                                />
                                            </Box>}
                                        </Box>
                                    </Box>
                                )
                            }) : <Box component={'div'} p={'0.5vw'} height={"40vh"}
                                display={"flex"} justifyContent={"center"} alignItems={"center"}>
                                No Records Found</Box>}
                        </Box>
                    </Box> : <Box height={"65vh"} p="0.5vw" display={"flex"} justifyContent={"center"} alignItems={"center"}>
                        No Records Found</Box>}
                    <Loader loaderOpen={props.is_loading} />
            </Box>
        </Modal >
    )
}

export default withTranslation()(PreviousDiagnosis);